// Progress bar component for hiring flow

import { CheckIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const STEP_KEYS = [
  { id: 1, nameKey: 'steps.details', descKey: 'steps.detailsDesc' },
  { id: 2, nameKey: 'steps.confirm', descKey: 'steps.confirmDesc' },
  { id: 3, nameKey: 'steps.signature', descKey: 'steps.signatureDesc' },
  { id: 4, nameKey: 'steps.payment', descKey: 'steps.paymentDesc' },
  { id: 5, nameKey: 'steps.completed', descKey: 'steps.completedDesc' },
] as const;

interface ProgressBarProps {
  currentStep: number;
}

export function ProgressBar({ currentStep }: ProgressBarProps) {
  const { t } = useTranslation('hiring');

  return (
    <div className="w-full py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Mobile: Simple progress */}
        <div className="md:hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {t('steps.stepOf', { current: currentStep, total: STEP_KEYS.length })}
            </span>
            <span className="text-xs text-gray-500">
              {t(STEP_KEYS[currentStep - 1]?.nameKey ?? 'steps.details')}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${(currentStep / STEP_KEYS.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Desktop: Full progress bar */}
        <div className="hidden md:flex items-center justify-between">
          {STEP_KEYS.map((step, index) => {
            const isCompleted = currentStep > step.id;
            const isCurrent = currentStep === step.id;
            const isUpcoming = currentStep < step.id;

            return (
              <div key={step.id} className="flex items-center flex-1">
                {/* Step circle */}
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all duration-300',
                      {
                        'bg-secondary text-white': isCompleted,
                        'bg-primary text-white ring-4 ring-primary/20': isCurrent,
                        'bg-gray-200 text-gray-500': isUpcoming,
                      }
                    )}
                  >
                    {isCompleted ? <CheckIcon width={24} height={24} /> : step.id}
                  </div>

                  {/* Step label */}
                  <div className="mt-2 text-center">
                    <p
                      className={cn('text-sm font-medium', {
                        'text-secondary': isCompleted,
                        'text-primary': isCurrent,
                        'text-gray-500': isUpcoming,
                      })}
                    >
                      {t(step.nameKey)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{t(step.descKey)}</p>
                  </div>
                </div>

                {/* Connector line */}
                {index < STEP_KEYS.length - 1 && (
                  <div className="flex-1 h-1 mx-4 relative top-[-20px]">
                    <div
                      className={cn('h-full transition-all duration-300', {
                        'bg-secondary': currentStep > step.id,
                        'bg-gray-200': currentStep <= step.id,
                      })}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
